/* eslint-disable react/require-default-props */
import React, { useMemo } from 'react';
import proptypes from 'prop-types';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { ImageHotspot } from '@thd-olt-component-react/image-hotspot';
import { VideoHeroImage } from '../../VideoHeroImage';
import { akamaiSrc, akamaiSrcSet, akamaiSrcSetDesktop } from '../../../utils/akamaiImageOptimizer';

const { shape, number, bool } = proptypes;

// IF NOT IN A CAROUSEL, PROP WILL NOT BE PASSED AND THIS WILL DEFAULT TO 0
const getImageUrl = ({ imageData, isHotspotImage, sourceData }) => {
  if (isHotspotImage) {
    if (sourceData) return sourceData.uri;

    return imageData?.imageHotspot?.imageUrl || '';
  }
  return imageData?.damDownloadedContent?.url
    || imageData?.damContentSelector?.assetData?.[0]?.selectedImageUrl
    || '';
};

const BayImage = React.memo(({
  bayImageData,
  isHotspotImage = false,
  position = 0,
  isVideo = false
}) => {
  const {
    mobilePreviewImage,
    previewImage,
    posterImage,
    desktopPosterImage,
    id,
    altText = '',
    title = '',
    shouldLoopVideo = true
  } = useMemo(
    () => bayImageData?.components?.[0] || bayImageData,
    [bayImageData]
  );

  const { sourceData: allSourceData } = bayImageData || {};

  const isMobileViewport = useBreakpoint('lg').lesser;
  const isLargerBayPosition = position === 1 || position === 3;

  const mobileImageUrl = useMemo(() => getImageUrl({
    imageData: mobilePreviewImage,
    isHotspotImage,
    sourceData: allSourceData?.mobile,
  }),
  [mobilePreviewImage, isHotspotImage, allSourceData],
  );
  const desktopImageUrl = useMemo(() => getImageUrl({
    imageData: previewImage,
    isHotspotImage,
    sourceData: allSourceData?.desktop
  }),
  [previewImage, isHotspotImage, allSourceData],
  );

  const mobilePosterImageUrl = posterImage ? getImageUrl({
    imageData: posterImage,
    isHotspotImage
  }) : null;
  const desktopPosterImageUrl = desktopPosterImage ? getImageUrl({
    imageData: desktopPosterImage,
    isHotspotImage
  }) : null;

  const imageAltText = altText || title;
  const videoClasses = 'sui-w-full sui-object-cover sui-h-full';

  const hotspotId = useMemo(() => (isMobileViewport
    ? mobilePreviewImage?.imageHotspot?.id
    : previewImage?.imageHotspot?.id),
  [isMobileViewport, mobilePreviewImage, previewImage]
  );

  const bayMediaContent = isVideo
    ? (
      <VideoHeroImage
        mobileVideoUrl={mobileImageUrl}
        desktopVideoUrl={desktopImageUrl}
        mobilePosterImageUrl={mobilePosterImageUrl}
        desktopPosterImageUrl={desktopPosterImageUrl}
        altText={imageAltText}
        fullWidth
        isBentoHero
        videoClasses={videoClasses}
        shouldLoopVideo={shouldLoopVideo}
        videoId={bayImageData?.components?.[0]?.id || bayImageData?.id}
      />
    ) : (
      <picture style={{ width: '100%' }}>
        <source
          media="(min-width: 1024px)"
          alt={imageAltText}
          className="sui-w-full sui-h-full sui-top-0 sui-left-0"
          srcSet={akamaiSrcSetDesktop(desktopImageUrl) || desktopImageUrl}
          sizes="100vw"
          data-testid="bay-image-desktop"
        />
        <img
          src={akamaiSrc(mobileImageUrl, 1024)}
          srcSet={akamaiSrcSet(mobileImageUrl)}
          sizes="100vw"
          alt={imageAltText}
          className="sui-w-full sui-h-full sui-top-0 sui-left-0"
          data-testid="bay-image-img"
          fetchPriority={isLargerBayPosition ? 'high' : 'low'}
          width="1"
          height="1"
        />
      </picture>
    );

  if (isHotspotImage) {
    const sourceData = isMobileViewport ? allSourceData?.mobile : allSourceData?.desktop;

    return (
      <ErrorBoundary name="image-hotspot">
        <QueryProvider cacheKey="image-hotspot">
          <span style={{ width: '100%' }}>
            <ImageHotspot
              componentId={hotspotId}
              componentClass="ImageHotspot"
              ssrImageData={{
                mobileImageSrc: mobileImageUrl,
                mobilePosterImageUrl,
                desktopPosterImageUrl,
                responsiveImages: [{
                  imageSrc: desktopImageUrl,
                  mediaQuery: '(min-width: 1024px)'
                }]
              }}
              enableSSR
              parentId={id}
              position={position}
              shouldLoopVideo={shouldLoopVideo}
              sourceData={sourceData}
            >
              {bayMediaContent}
            </ImageHotspot>
          </span>
        </QueryProvider>
      </ErrorBoundary>
    );
  }

  return bayMediaContent;
});

BayImage.propTypes = {
  bayImageData: shape({}).isRequired,
  position: number,
  isHotspotImage: bool,
  isVideo: bool
};

BayImage.displayName = 'BayImage';

export { BayImage };
